/**
 * SettingsProfilePage — профиль текущего пользователя.
 * Имя, локаль (UA/RU/EN), аватар. Email read-only.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Camera, Save } from 'lucide-react';

import { useMe, useUpdateMe, type AccountLocale } from '@/hooks/useAccount';

const LOCALES: { value: AccountLocale; label: string }[] = [
  { value: 'ua', label: 'Українська' },
  { value: 'ru', label: 'Русский' },
  { value: 'en', label: 'English' },
];

export function SettingsProfilePage() {
  const { i18n } = useTranslation();
  const { data: me, isLoading } = useMe();
  const update = useUpdateMe();

  const [fullName, setFullName] = useState('');
  const [locale, setLocale] = useState<AccountLocale>('ru');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [savedOk, setSavedOk] = useState(false);

  useEffect(() => {
    if (me) {
      setFullName(me.full_name);
      setLocale(me.locale);
      setAvatarUrl(me.avatar_url ?? '');
    }
  }, [me]);

  // Синхронизируем i18n при смене локали
  useEffect(() => {
    if (me && i18n.language !== me.locale) {
      i18n.changeLanguage(me.locale);
    }
  }, [me, i18n]);

  if (isLoading || !me) {
    return <div className="text-sm text-surface-400">Loading profile…</div>;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavedOk(false);
    await update.mutateAsync({
      full_name: fullName,
      locale,
      avatar_url: avatarUrl || null,
    });
    // i18n подхватится автоматически (locale сменился)
    setSavedOk(true);
    setTimeout(() => setSavedOk(false), 2500);
  };

  const dirty =
    fullName !== me.full_name ||
    locale !== me.locale ||
    (avatarUrl || null) !== (me.avatar_url ?? null);

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold mb-1">Профиль</h1>
        <p className="text-sm text-surface-400">
          Имя, язык интерфейса, аватар. Email изменить нельзя.
        </p>
      </div>

      <div className="card flex items-center gap-4">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-orange flex items-center justify-center text-white text-xl font-semibold overflow-hidden shrink-0">
          {me.avatar_url ? (
            <img src={me.avatar_url} alt={me.full_name} className="w-full h-full object-cover" />
          ) : (
            <User className="w-7 h-7" />
          )}
        </div>
        <div>
          <div className="font-semibold">{me.full_name}</div>
          <div className="text-sm text-surface-400">{me.email}</div>
          <div className="text-xs text-surface-500 mt-1">
            role: <span className="font-mono">{me.role}</span>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="card space-y-4">
        <div>
          <label className="text-xs text-surface-400 mb-1 block">Email</label>
          <input className="input w-full bg-surface-800/40 cursor-not-allowed" value={me.email} disabled readOnly />
        </div>
        <div>
          <label className="text-xs text-surface-400 mb-1 block">Имя</label>
          <input
            className="input w-full"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="text-xs text-surface-400 mb-1 block">Язык интерфейса</label>
          <select
            className="input w-full"
            value={locale}
            onChange={(e) => setLocale(e.target.value as AccountLocale)}
          >
            {LOCALES.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
          <div className="text-xs text-surface-500 mt-1">
            Язык интерфейса ≠ язык генерации. Язык генерации выбирается отдельно при запуске.
          </div>
        </div>
        <div>
          <label className="text-xs text-surface-400 mb-1 block">Avatar URL</label>
          <div className="flex items-center gap-2">
            <Camera className="w-4 h-4 text-surface-500" />
            <input
              className="input flex-1"
              value={avatarUrl}
              onChange={(e) => setAvatarUrl(e.target.value)}
              placeholder="https://..."
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button type="submit" disabled={!dirty || update.isPending} className="btn-primary flex items-center gap-2">
            <Save className="w-4 h-4" />
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
          {savedOk && <span className="text-sm text-success">Saved</span>}
        </div>
      </form>
    </div>
  );
}
